import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  BehaviorSubject,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { Representative } from 'src/app/shared/models/dtos/register-form/representative/representative';
import { RepresentativeRole } from 'src/app/shared/models/dtos/register-form/representative/representative-role';
import { RepresentativeUpdateRequestDTO } from 'src/app/shared/models/dtos/requests/representative-update-request-dto/representative-update-request-dto';
import { genericRegisterValidator } from 'src/app/shared/validators/registration-validators';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  selector: 'app-edit-account',
  templateUrl: './edit-account.component.html',
  styleUrls: ['./edit-account.component.scss'],
})

/**
 * Edit account form
 */
export class EditAccountComponent implements OnInit, OnDestroy, AfterContentChecked { // eslint-disable-line
  previousAccountData: any;
  representative$: BehaviorSubject<Representative | undefined>;
  editAccountFormGroup = new FormGroup({
    profilePicture: new FormControl(''),
    name: new FormControl('', [
      genericRegisterValidator(),
      Validators.minLength(3),
    ]),
    lastName: new FormControl('', [
      genericRegisterValidator(),
      Validators.minLength(3),
    ]),
    email: new FormControl('', [
      Validators.email,
      genericRegisterValidator('email'),
    ]),
  });
  loading: boolean = false;
  RoutesKeys = RoutesKeys;
  labels = {
    uploadImage: 'upload-image',
    changeImage: 'change-image',
    uploadImageAlt: 'upload-image-alt',
    uploadedImageAlt: 'uploaded-image-alt',
    personalData: 'personal-data',
    name: 'name',
    lastName: 'last-name',
    emailAddress: 'email-address',
    saveAccountChanges: 'save-account-changes',
    updateSuccessful: 'success-updating-data',
    updateFailed: 'error-updating-data',
    password: 'password',
    clickHereChangePassword: 'click-here-to-change-your-password',
    deleteYourAccount: 'delete-your-account',
    clickHereDeleteAccount: 'click-here-delete-account',
    deleteAccountRequested: 'delete-account-requested',
    deleteAccountRequest: 'delete-account-request',
    pending: 'pending',
    deleteAccountRequestStatus: 'delete-account-request-status',
    deleteAccountUnableText: 'delete-account-unable-text',
    noAvailableRepresentative: 'no-available-representative',
  };
  isAdmin: boolean = false;
  canDisableAcc: boolean = false;
  private readonly subsink = new Subscription();
  partPreviousAccountData: any;

  /**
   * @constructor
   */
  constructor(
    private translationService: TranslationService,
    private ref: ChangeDetectorRef,
    private userService: UserService,
    private snackBarService: SnackBarService,
    private authenticatedUserService: AuthenticatedUserService
  ) {
    this.representative$ = authenticatedUserService.representative$;
  }

  /**
   * ngAfterContentChecked
   */
  ngAfterContentChecked() {
    this.ref.detectChanges();
  }

  /**
   * on init
   */
  ngOnInit(): void {
    this.subsink.add(
      this.representative$
        .pipe(
          filter((info) => info != null),
          tap((info) => {
            this.editAccountFormGroup.patchValue({
              profilePicture: info!.picture,
              name: info!.firstName,
              lastName: info!.lastName,
              email: info!.email,
            });
            this.previousAccountData = this.editAccountFormGroup.value;
          }),
          switchMap((info) => {
            if (info?.role === RepresentativeRole.BUSINESS_REPRESENTATIVE) {
              return of(true);
            }
            return this.canDisable();
          })
        )
        .subscribe((canDisable) => {
          this.canDisableAcc = canDisable;
        })
    );
    this.translationService.translateObject(this.labels);
    this.isAdmin = this.authenticatedUserService.isAdmin();
  }

  /**
   * cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * A shortcut for profile picture form control
   */
  get profilePictureFormControl() {
    return this.editAccountFormGroup.controls['profilePicture'];
  }

  /**
   * A shortcut for name form control
   */
  get nameFormControl() {
    return this.editAccountFormGroup.controls['name'];
  }

  /**
   * A shortcut for last name form control
   */
  get lastNameFormControl() {
    return this.editAccountFormGroup.controls['lastName'];
  }

  /**
   * A shortcut for e-mail form control
   */
  get emailFormControl() {
    return this.editAccountFormGroup.controls['email'];
  }

  /**
   * The form must be disabled if there's no changes in the data or it is invalid
   * @return {boolean}
   */
  isFormDisabled() {
    const newAccountData = JSON.stringify(this.editAccountFormGroup.value);

    const partPreviousAccountData = {
      profilePicture: this.representative$.getValue()?.picture,
      name: this.representative$.getValue()?.firstName,
      lastName: this.representative$.getValue()?.lastName,
      email: this.representative$.getValue()?.email,
    };

    const previousAccountData = JSON.stringify(partPreviousAccountData);

    return (
      newAccountData === previousAccountData ||
      this.editAccountFormGroup.invalid
    );
  }

  /**
   * Update account data
   */
  saveAccountChanges() {
    const representativeData = this.representative$.value;
    if (representativeData == null) {
      throw new Error(this.labels.noAvailableRepresentative);
    }
    if (representativeData.language === null) {
      representativeData.language = 'en';
    }
    this.loading = true;
    const representativeDTO = new RepresentativeUpdateRequestDTO(
      this.editAccountFormGroup.get('name')?.value,
      this.editAccountFormGroup.get('lastName')?.value,
      this.editAccountFormGroup.get('email')?.value,
      this.editAccountFormGroup.get('profilePicture')?.value,
      representativeData.language
    );
    this.userService
      .updateRepresentative(representativeDTO)
      .pipe(
        filter((res) => res != null),
        take(1)
      )
      .subscribe({
        next: (representative) => {
          this.snackBarService.success(this.labels.updateSuccessful);
          this.loading = false;
          this.previousAccountData = this.editAccountFormGroup.value;
          this.authenticatedUserService.representative = representative;
        },
        error: () => {
          this.snackBarService.error(this.labels.updateFailed);
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  /**
   * If there is a delete account request pending for this user
   * @return {boolean}
   */
  get isDeleteAccountRequestPending(): boolean {
    // TODO: backend integration
    return false;
  }

  /**
   * Delete account header text
   * @return {string}
   */
  deleteAccountHeader(): string {
    if (this.isDeleteAccountRequestPending) {
      return this.labels.deleteAccountRequested;
    } else {
      return this.labels.deleteYourAccount;
    }
  }

  /**
   * Check if the user can disable its account
   * @return {Observable<boolean>}
   */
  canDisable(): Observable<boolean> {
    return forkJoin({
      invitees: this.userService.listInvitees(),
      representatives: this.userService.listRepresentatives(),
    }).pipe(
      map(
        ({ invitees, representatives }) =>
          invitees.length === 0 &&
          representatives.filter((rep) => rep.active).length === 1
      )
    );
  }
}
